import { ArrowDownIcon, CheckIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { useFrame } from '../engine'
import { anim, lerp, seg, smooth } from '../motion'
import { COLORS, Count, Shape } from '../primitives'
import { BEATS, atB, inB } from './beats'
import { Envelope, Flight, Heart, MiniPhone, Person, Walker } from './figures'
import { H, U, W, iso, onWallA } from './iso'
import {
  AnalysisScreen,
  AtsPanel,
  FairPanel,
  GamePanel,
  InterviewCard,
  MagicPanel,
  OptionCards,
  PdfPanel,
  ProfilePanel,
  RulePanel,
  SupportPanel,
  TalentPhone
} from './panels'
import { MACHINE_GLASS, OfficeWorld, PEOPLE, SPOTS } from './world'

const range = PropTypes.arrayOf(PropTypes.number)

// ---------- Kamera ----------
// f = Fokuspunkt in Welteinheiten, z = Zoom, ox = Versatz in Bildschirmbreiten
// (negativ schiebt das Büro nach links, wenn rechts ein Panel steht)

const shot = (l, f, z, ox = 0) => ({ l, f, z, ox })
const OVERVIEW = [13, 11, 11]
const MONITOR = [SPOTS.monitor.x, SPOTS.monitor.y, SPOTS.monitor.z]

const SHOTS = [
  shot(0, OVERVIEW, 0.8),
  shot(atB('intro', 0.6), OVERVIEW, 0.8),
  shot(atB('vorstory', 0.2), [16, 8, 2], 1.25),
  shot(atB('vorstory', 0.9), [16, 8, 2], 1.25),
  shot(atB('hr', 0.15), [11, 8.5, 3.2], 1.55),
  shot(atB('hr', 0.92), [11, 8.5, 3.2], 1.55),
  shot(atB('analyse', 0.12), [12, 11, 2.4], 1.12),
  shot(atB('analyse', 0.95), [12, 11, 2.4], 1.12),
  shot(atB('profil', 0.15), [9, 6, 2], 1.1, -0.22),
  shot(atB('profil', 0.9), [9, 6, 2], 1.1, -0.22),
  shot(atB('bewerbungen', 0.12), [14, 7, 1], 1.2),
  shot(atB('bewerbungen', 0.9), [14, 7, 1], 1.2),
  shot(atB('games', 0.12), [11, 3.5, 2], 1.25, -0.2),
  shot(atB('games', 0.92), [11, 3.5, 2], 1.25, -0.2),
  shot(atB('magic', 0.14), [5, 4, 5], 2),
  shot(atB('magic', 0.92), [5, 4, 5], 2),
  shot(atB('regeln', 0.15), [5, 4, 1.6], 1.6),
  shot(atB('regeln', 0.9), [5, 4, 1.6], 1.6),
  shot(atB('talente', 0.15), [20, 0.8, 3.6], 1.45),
  shot(atB('talente', 0.9), [20, 0.8, 3.6], 1.45),
  shot(atB('report', 0.12), [12, 0, 3.6], 1.9),
  shot(atB('report', 0.26), [12, 0, 3.6], 1.9),
  shot(atB('report', 0.4), [12, 0, 3.6], 1.5, -0.24),
  shot(atB('report', 0.92), [12, 0, 3.6], 1.5, -0.24),
  shot(atB('gespraech', 0.15), [17, 8, 1], 1.15),
  shot(atB('gespraech', 0.32), [15, 12, 1], 1.2),
  shot(atB('gespraech', 0.45), [12, 18.4, 1.6], 1.95),
  shot(atB('gespraech', 0.95), [12, 18.4, 1.6], 1.95),
  shot(atB('team', 0.3), [19, 14.5, 1], 1.7),
  shot(atB('team', 0.95), [19, 14.5, 1], 1.7),
  shot(atB('absagen', 0.15), [15, 4, 1.5], 1.2, -0.2),
  shot(atB('absagen', 0.92), [15, 4, 1.5], 1.2, -0.2),
  shot(atB('ats', 0.15), MONITOR, 2.2),
  shot(atB('ats', 0.95), MONITOR, 2.2),
  shot(atB('support', 0.2), [11, 8.5, 1.5], 1.8, -0.2),
  shot(atB('support', 0.95), [11, 8.5, 1.5], 1.8, -0.2),
  shot(atB('finale', 0.25), OVERVIEW, 0.8),
  shot(1, OVERVIEW, 0.8)
]

function camera(l) {
  let i = 1
  while (i < SHOTS.length - 1 && l > SHOTS[i].l) i++
  const a = SHOTS[i - 1]
  const b = SHOTS[i]
  const t = smooth(seg(l, a.l, b.l))
  const pa = iso(...a.f)
  const pb = iso(...b.f)
  return {
    x: lerp(pa.x, pb.x, t),
    y: lerp(pa.y, pb.y, t),
    z: Math.exp(lerp(Math.log(a.z), Math.log(b.z), t)),
    ox: lerp(a.ox, b.ox, t)
  }
}

// ---------- Fliegende Dinge ----------

const rnd = (i, s) => {
  const v = Math.sin(i * 127.1 + s * 311.7) * 43758.5453
  return v - Math.floor(v)
}

const head = (p) => {
  const s = iso(p.x, p.y)
  return { x: s.x, y: s.y - 52 }
}
const person = (id) => PEOPLE.find((p) => p.id === id)

const DOOR = iso(SPOTS.door.x, SPOTS.door.y, 2.2)
const MACHINE_TOP = iso(5, 4, 3.4)
const MARA = head(SPOTS.mara)
const BOARD_MID = iso(SPOTS.board.x + 3.5, 0, 4)
const EXPERTS = ['csLead', 'csMate', 'vLead']
const KINDS = ['tri', 'sq', 'dot']
const TONES = ['yellow', 'green', 'pink']

const TEMPLATE = [
  {
    legs: [
      {
        from: MACHINE_TOP,
        to: { x: MARA.x, y: MARA.y - 40 },
        at: inB('hr', 0.16, 0.3),
        arc: 80
      }
    ]
  }
]

const INVITES = EXPERTS.map((id, i) => ({
  legs: [
    {
      from: MARA,
      to: head(person(id)),
      at: inB('analyse', 0.04 + i * 0.04, 0.18 + i * 0.04),
      arc: 70,
      spin: 20
    }
  ]
}))

const ANALYSES = EXPERTS.flatMap((id, i) =>
  [0, 1].map((k) => ({
    kind: KINDS[(i + k) % 3],
    color: TONES[(i + k) % 3],
    legs: [
      {
        from: head(person(id)),
        to: MACHINE_TOP,
        at: inB(
          'analyse',
          0.74 + i * 0.03 + k * 0.02,
          0.88 + i * 0.03 + k * 0.02
        ),
        arc: 90,
        spin: 180
      }
    ]
  }))
)

const LETTERS = Array.from({ length: 96 }, (_, i) => {
  const x = 5.5 + rnd(i, 1) * 11.5
  let y = 4 + rnd(i, 2) * 10.5
  // Nicht auf dem HR-Schreibtisch landen
  if (x > 9 && x < 12.4 && y > 7 && y < 10.4) y += 3.4
  const pile = iso(x, y, 0)
  const s = i / 96
  return {
    rot: rnd(i, 3) * 70 - 35,
    legs: [
      {
        from: DOOR,
        to: { x: pile.x, y: pile.y - 6 },
        at: inB('bewerbungen', 0.06 + s * 0.62, 0.22 + s * 0.62),
        arc: 120 + rnd(i, 4) * 80,
        spin: rnd(i, 5) * 360 - 180
      },
      {
        from: { x: pile.x, y: pile.y - 6 },
        to: MACHINE_TOP,
        at: inB('games', s * 0.08, 0.1 + s * 0.08),
        arc: 70,
        shrink: true
      }
    ]
  }
})

const PHONES = Array.from({ length: 12 }, (_, i) => ({
  kind: KINDS[i % 3],
  color: TONES[i % 3],
  legs: [
    {
      from: MACHINE_TOP,
      to: DOOR,
      at: inB('games', 0.14 + i * 0.025, 0.3 + i * 0.025),
      arc: 130 + (i % 4) * 25,
      spin: 20
    }
  ]
}))

const RESULTS = Array.from({ length: 12 }, (_, i) => ({
  kind: KINDS[(i + 1) % 3],
  color: TONES[(i + 2) % 3],
  legs: [
    {
      from: DOOR,
      to: MACHINE_TOP,
      at: inB('games', 0.56 + i * 0.022, 0.7 + i * 0.022),
      arc: 110 + (i % 3) * 30,
      spin: 180
    }
  ]
}))

// Im Schnittmodell rieseln die Ergebnisse durch den Würfel
const MACHINE_FLOOR = iso(5, 4, 0.4)
const MAGIC_FLOW = Array.from({ length: 14 }, (_, i) => ({
  kind: KINDS[i % 3],
  color: TONES[(i + 1) % 3],
  legs: [
    {
      from: { x: MACHINE_TOP.x + ((i % 5) - 2) * 14, y: MACHINE_TOP.y },
      to: { x: MACHINE_FLOOR.x + ((i % 3) - 1) * 18, y: MACHINE_FLOOR.y },
      at: inB('magic', 0.16 + i * 0.045, 0.3 + i * 0.045),
      spin: 120
    }
  ]
}))

const PROFILES_OUT = Array.from({ length: 8 }, (_, i) => ({
  legs: [
    {
      from: MACHINE_TOP,
      to: DOOR,
      at: inB('talente', 0.04 + i * 0.03, 0.2 + i * 0.03),
      arc: 110,
      spin: 10
    }
  ]
}))

const CARDS = Array.from({ length: 3 }, (_, i) => ({
  legs: [
    {
      from: MACHINE_TOP,
      to: { x: BOARD_MID.x, y: BOARD_MID.y + i * 14 },
      at: inB('report', 0.02 + i * 0.04, 0.16 + i * 0.04),
      arc: 60
    }
  ]
}))

const REJECTIONS = Array.from({ length: 10 }, (_, i) => ({
  legs: [
    {
      from: MARA,
      to: DOOR,
      at: inB('absagen', 0.06 + i * 0.03, 0.24 + i * 0.03),
      arc: 110 + (i % 3) * 30,
      spin: 15
    }
  ]
}))

const renderShape = (it) => (
  <g transform='translate(-9 -9)'>
    <Shape kind={it.kind} color={it.color} size={18} />
  </g>
)

// ---------- Overlays in Weltkoordinaten ----------

function Anchor({ at3, dy = 0, children }) {
  const p = iso(...at3)
  return (
    <div className='off-anchor' style={{ left: p.x, top: p.y - dy }}>
      {children}
    </div>
  )
}

Anchor.propTypes = {
  at3: range.isRequired,
  dy: PropTypes.number,
  children: PropTypes.node
}

function Bubble({ at3, dx = 0, dy = 84, show, hide, tone, children }) {
  return (
    <Anchor at3={at3} dy={dy}>
      <div
        className={clsx('off-bubble', tone && `off-bubble--${tone}`)}
        style={{
          marginLeft: dx,
          ...anim({ at: show, out: hide, y: 14, oy: -10, s: 0.7 })
        }}
      >
        {children}
      </div>
    </Anchor>
  )
}

Bubble.propTypes = {
  at3: range.isRequired,
  dx: PropTypes.number,
  dy: PropTypes.number,
  show: range.isRequired,
  hide: range,
  tone: PropTypes.string,
  children: PropTypes.node
}

const SHORTLIST = [
  { name: 'Lena K.', value: 91, color: 'pink', top: true },
  { name: 'Aylin S.', value: 84, color: 'yellow' },
  { name: 'Jonas M.', value: 79, color: 'green' }
]

function Board() {
  const b = SPOTS.board
  return (
    <div
      className='off-board'
      style={{
        width: b.w * U,
        height: (b.z1 - b.z0) * U,
        transform: onWallA(iso(b.x, 0, b.z1))
      }}
    >
      <div
        className='off-board__idle'
        style={anim({
          at: [-1, 0],
          out: inB('report', 0.02, 0.1),
          y: 0,
          oy: 0
        })}
      >
        <span>Wir suchen</span>
        <strong>Customer Success Manager:in</strong>
      </div>
      <div
        className='off-board__list'
        style={anim({ at: inB('report', 0.12, 0.2), y: 0 })}
      >
        <div className='off-board__head'>Shortlist · Customer Success</div>
        {SHORTLIST.map((s, i) => (
          <div
            key={s.name}
            className={clsx('off-board__row', s.top && 'is-top')}
            style={anim({
              at: inB('report', 0.18 + i * 0.04, 0.24 + i * 0.04),
              x: -20,
              y: 0
            })}
          >
            <i style={{ background: COLORS[s.color] }} />
            <span>{s.name}</span>
            <b>{s.value}&nbsp;%</b>
          </div>
        ))}
      </div>
    </div>
  )
}

const QUESTIONS = [
  'Wie wichtig ist Emotionswahrnehmung?',
  'Wie wichtig ist Teamarbeit?',
  'Wie wichtig ist Gewissenhaftigkeit?'
]

function Overlays() {
  const mara = [SPOTS.mara.x, SPOTS.mara.y, 0]
  const at = (id) => {
    const p = person(id)
    return [p.x, p.y, 0]
  }

  return (
    <div className='off-overlay'>
      <div
        className='off-machine-label'
        style={{
          width: 3 * U,
          transform: onWallA(iso(3.5, 5.5, 2.1)),
          ...MACHINE_GLASS
        }}
      >
        aivy
      </div>
      <Board />

      {/* Die offene Stelle */}
      <Anchor at3={[SPOTS.lenaSeat.x, SPOTS.lenaSeat.y, 0]} dy={40}>
        <div
          className='off-seat'
          style={anim({
            at: inB('vorstory', 0.12, 0.24),
            out: inB('team', 0.3, 0.42),
            s: 0.3,
            y: 10
          })}
        >
          ?
        </div>
      </Anchor>
      <Bubble
        at3={at('csLead')}
        show={inB('vorstory', 0.3, 0.42)}
        hide={inB('vorstory', 0.86, 0.96)}
        tone='green'
      >
        Wir brauchen dringend Verstärkung!
      </Bubble>

      {/* HR startet mit Aivy */}
      <Bubble
        at3={mara}
        show={inB('hr', 0.04, 0.12)}
        hide={inB('hr', 0.16, 0.22)}
        tone='blue'
      >
        Für die Vorauswahl hole ich mir Aivy dazu.
      </Bubble>
      <Anchor at3={mara} dy={86}>
        <div
          style={anim({
            at: inB('hr', 0.2, 0.24),
            out: inB('hr', 0.9, 1),
            y: 0
          })}
        >
          <OptionCards />
        </div>
      </Anchor>

      {/* Anforderungsanalyse */}
      {EXPERTS.map((id, i) => (
        <Anchor key={id} at3={at(id)} dy={86}>
          <div style={{ marginLeft: [-70, 80, 0][i] }}>
            <AnalysisScreen delay={i * 0.04} question={QUESTIONS[i]} />
          </div>
        </Anchor>
      ))}
      <Anchor at3={mara} dy={86}>
        <div
          className='off-tag off-tag--green'
          style={anim({
            at: inB('analyse', 0.42, 0.52),
            out: inB('analyse', 0.88, 0.96),
            y: 10,
            s: 0.6
          })}
        >
          <CheckIcon width={13} height={13} /> Kein Aufwand für HR
        </div>
      </Anchor>
      <Anchor at3={[5, 4, 3.4]} dy={60}>
        <div
          className='off-tag off-tag--blue'
          style={anim({
            at: inB('analyse', 0.88, 0.96),
            out: inB('profil', 0.1, 0.2),
            y: 10,
            s: 0.6
          })}
        >
          <CheckIcon width={13} height={13} /> Expert:innen stimmen überein
        </div>
      </Anchor>

      {/* Bewerbungen */}
      <Anchor at3={[SPOTS.door.x, SPOTS.door.y, 4.6]} dy={10}>
        <div
          className='off-counter'
          style={anim({
            at: inB('bewerbungen', 0.06, 0.14),
            out: inB('bewerbungen', 0.94, 1),
            y: 12,
            s: 0.7
          })}
        >
          <Count
            to={128}
            at={inB('bewerbungen', 0.08, 0.8)}
            className='off-counter__num'
          />
          <span>Bewerbungen</span>
        </div>
      </Anchor>

      {/* Game-based Assessment */}
      <Anchor at3={[5, 4, 3.4]} dy={70}>
        <div
          className='off-counter off-counter--blue'
          style={anim({
            at: inB('games', 0.16, 0.24),
            out: inB('games', 0.88, 0.96),
            y: 12,
            s: 0.7
          })}
        >
          <span>Game-based Assessment</span>
          <small>an alle Bewerbenden</small>
        </div>
      </Anchor>

      {/* Blick in Aivy */}
      <Anchor at3={[5, 4, 3.4]} dy={40}>
        <MagicPanel />
      </Anchor>

      {/* Bewerbende */}
      <Anchor at3={[18.6, 0.3, 1.2]}>
        <TalentPhone />
      </Anchor>
      <Bubble
        at3={[SPOTS.door.x + 0.6, SPOTS.door.y, 5]}
        dx={90}
        dy={0}
        show={inB('talente', 0.42, 0.5)}
        hide={inB('talente', 0.9, 1)}
        tone='pink'
      >
        Hat richtig Spaß gemacht!
      </Bubble>
      <Bubble
        at3={[SPOTS.door.x + 0.6, SPOTS.door.y, 2.4]}
        dx={130}
        dy={0}
        show={inB('talente', 0.54, 0.62)}
        hide={inB('talente', 0.9, 1)}
        tone='yellow'
      >
        Endlich mal kein langweiliger Fragebogen.
      </Bubble>

      {/* Gespräch */}
      <Anchor at3={[SPOTS.maraTalk.x, SPOTS.maraTalk.y, 0]} dy={92}>
        <InterviewCard />
      </Anchor>
      <Bubble
        at3={[SPOTS.lenaTalk.x, SPOTS.lenaTalk.y, 0]}
        dx={40}
        show={inB('gespraech', 0.66, 0.72)}
        hide={inB('gespraech', 0.9, 1)}
        tone='pink'
      >
        Gern, da gab es einen Fall …
      </Bubble>

      {/* Team */}
      <Bubble
        at3={at('csLead')}
        show={inB('team', 0.6, 0.7)}
        hide={inB('team', 0.9, 1)}
        tone='green'
      >
        Willkommen im Team, Lena!
      </Bubble>
    </div>
  )
}

// ---------- Bühne mit Kamera ----------

export default function OfficeStory() {
  const stage = useRef(null)
  const world = useRef(null)
  const last = useRef(0)
  const [beat, setBeat] = useState(0)

  const place = (l) => {
    const host = stage.current
    if (!host || !world.current) return
    // Schmale Screens: näher ran, sonst werden die Figuren winzig
    const narrow = host.clientWidth < 700
    const fit =
      Math.min(host.clientWidth / W, host.clientHeight / H) *
      (narrow ? 1.9 : 1.08)
    const cam = camera(l)
    const s = fit * cam.z
    const cx = host.clientWidth * (0.5 + (narrow ? 0 : cam.ox))
    const cy = host.clientHeight * (narrow ? 0.4 : 0.48)
    world.current.style.transform = `translate(${cx - cam.x * s}px, ${cy - cam.y * s}px) scale(${s})`
  }

  useFrame((l) => {
    last.current = l
    place(l)
    const i = BEATS.findIndex((b) => l >= b.at[0] && l < b.at[1])
    setBeat(i === -1 ? BEATS.length - 1 : i)
  })

  useEffect(() => {
    const ro = new ResizeObserver(() => place(last.current))
    if (stage.current) ro.observe(stage.current)
    return () => ro.disconnect()
  }, [])

  const jump = (i) => {
    const section = stage.current?.closest('.off-section')
    if (!section) return
    const top = section.getBoundingClientRect().top + window.scrollY
    const [s, e] = BEATS[i].at
    const target =
      top + (s + (e - s) * 0.45) * (section.offsetHeight - window.innerHeight)
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({
      top: i === 0 ? top : target,
      behavior: reduce ? 'auto' : 'smooth'
    })
  }

  const toTop = () => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' })
  }

  const lenaPath = [
    { at: atB('gespraech', 0.02), ...SPOTS.door },
    { at: atB('gespraech', 0.14), x: 22.2, y: 5.6 },
    { at: atB('gespraech', 0.26), x: 15.4, y: 6.6 },
    { at: atB('gespraech', 0.38), x: 14.6, y: 14.6 },
    { at: atB('gespraech', 0.46), ...SPOTS.lenaTalk },
    { at: atB('team', 0.04), ...SPOTS.lenaTalk },
    { at: atB('team', 0.2), x: 17.4, y: 19.4 },
    { at: atB('team', 0.36), x: 20.4, y: 18.4 },
    { at: atB('team', 0.5), ...SPOTS.lenaSeat }
  ]
  const maraPath = [
    { at: atB('gespraech', 0.18), ...SPOTS.mara },
    { at: atB('gespraech', 0.32), x: 10.2, y: 13.4 },
    { at: atB('gespraech', 0.46), ...SPOTS.maraTalk },
    { at: atB('team', 0.8), ...SPOTS.maraTalk },
    { at: atB('absagen', 0), x: 10.2, y: 13.4 },
    { at: atB('absagen', 0.12), ...SPOTS.mara }
  ]

  return (
    <div ref={stage} className='off-stage'>
      <div ref={world} className='off-world' style={{ width: W, height: H }}>
        <svg
          width={W}
          height={H}
          viewBox={`0 0 ${W} ${H}`}
          className='off-svg'
          aria-hidden='true'
        >
          <OfficeWorld />

          <Flight
            items={TEMPLATE}
            render={() => (
              <g>
                <rect
                  x='-14'
                  y='-18'
                  width='28'
                  height='36'
                  rx='4'
                  fill='#FFFFFF'
                  stroke={COLORS.blue}
                  strokeWidth='2'
                />
                <rect
                  x='-8'
                  y='-10'
                  width='16'
                  height='3'
                  rx='1.5'
                  fill={COLORS.blue}
                />
                <rect
                  x='-8'
                  y='-3'
                  width='12'
                  height='3'
                  rx='1.5'
                  fill='#C9D2E6'
                />
                <rect
                  x='-8'
                  y='4'
                  width='14'
                  height='3'
                  rx='1.5'
                  fill='#C9D2E6'
                />
              </g>
            )}
          />
          <Flight items={INVITES} render={() => <Envelope />} />
          <Flight items={ANALYSES} render={renderShape} />
          <Flight
            items={LETTERS}
            render={(it) => (
              <g transform={`rotate(${it.rot})`}>
                <Envelope />
              </g>
            )}
          />
          <Flight
            items={PHONES}
            render={(it) => <MiniPhone shape={it.kind} color={it.color} />}
          />
          <Flight items={RESULTS} render={renderShape} />
          <Flight items={MAGIC_FLOW} render={renderShape} />
          <Flight
            items={PROFILES_OUT}
            render={() => (
              <g>
                <rect
                  x='-11'
                  y='-14'
                  width='22'
                  height='28'
                  rx='3'
                  fill='#FFFFFF'
                  stroke={COLORS.pink}
                  strokeWidth='2'
                />
                <circle cx='0' cy='-4' r='4' fill={COLORS.pink} />
                <rect
                  x='-6'
                  y='4'
                  width='12'
                  height='3'
                  rx='1.5'
                  fill='#C9D2E6'
                />
              </g>
            )}
          />
          <Flight
            items={CARDS}
            render={() => (
              <g>
                <rect
                  x='-22'
                  y='-9'
                  width='44'
                  height='18'
                  rx='4'
                  fill='#FFFFFF'
                  stroke='#C9D2E6'
                />
                <circle cx='-13' cy='0' r='4' fill={COLORS.pink} />
                <rect
                  x='-6'
                  y='-2'
                  width='20'
                  height='4'
                  rx='2'
                  fill='#C9D2E6'
                />
              </g>
            )}
          />
          <Flight
            items={REJECTIONS}
            render={() => (
              <g>
                <Envelope />
                <circle cx='9' cy='-7' r='6' fill={COLORS.green} />
                <path
                  d='M6.5 -7 L8.5 -5 L11.8 -8.8'
                  fill='none'
                  stroke='#FFFFFF'
                  strokeWidth='1.6'
                  strokeLinecap='round'
                />
              </g>
            )}
          />

          <Walker path={maraPath}>
            <Person
              body='blue'
              skin='#E0AC69'
              hair='#5A3825'
              happy={inB('analyse', 0.46, 0.56)}
              sweat={[
                ...inB('bewerbungen', 0.4, 0.5),
                ...inB('games', 0.6, 0.72)
              ]}
            />
            <Heart
              x={-14}
              y={-80}
              at={inB('gespraech', 0.76, 0.82)}
              out={inB('gespraech', 0.94, 1)}
            />
            <Heart x={0} y={-78} at={inB('finale', 0.15, 0.32)} />
          </Walker>
          <g style={anim({ at: inB('gespraech', 0, 0.04), y: 0 })}>
            <Walker path={lenaPath}>
              <Person
                body='pink'
                skin='#F1D3B3'
                hair='#2B1D14'
                happy={inB('gespraech', 0.7, 0.78)}
              />
              <Heart
                x={12}
                y={-82}
                at={inB('gespraech', 0.78, 0.84)}
                out={inB('gespraech', 0.94, 1)}
              />
              <Heart
                x={0}
                y={-80}
                at={inB('team', 0.62, 0.72)}
                out={inB('team', 0.94, 1)}
              />
              <Heart x={0} y={-80} at={inB('finale', 0.15, 0.32)} />
            </Walker>
          </g>

          {['csLead', 'csMate', 'csNew'].map((id, i) => {
            const s = iso(person(id).x, person(id).y)
            return (
              <Heart
                key={id}
                x={s.x + (i - 1) * 4}
                y={s.y - 80}
                at={inB('team', 0.64 + i * 0.05, 0.74 + i * 0.05)}
                out={inB('team', 0.94, 1)}
              />
            )
          })}
        </svg>

        <Overlays />
      </div>

      {/* Bildschirm-Ebene */}
      <nav className='off-nav' aria-label='Kapitel'>
        {BEATS.map((b, i) => (
          <button
            key={b.id}
            type='button'
            className={clsx('off-nav__dot', i === beat && 'is-active')}
            onClick={() => jump(i)}
            aria-label={b.label}
            title={b.label}
          />
        ))}
        <span className='off-nav__label'>{BEATS[beat].label}</span>
      </nav>

      <div
        className='off-intro'
        style={anim({ at: [-1, 0], out: inB('intro', 0.6, 1), oy: -40 })}
      >
        <div className='off-eyebrow'>Recruiting mit Aivy</div>
        <h2 className='off-title'>
          Von der offenen Stelle zum richtigen Match.
        </h2>
        <p>
          Scroll und schau zu, wie Aivy die passende Person ins Team bringt.
        </p>
        <div className='off-cue'>
          <ArrowDownIcon width={16} height={16} /> Scroll los
        </div>
      </div>

      <ProfilePanel />
      <GamePanel />
      <RulePanel />
      <PdfPanel />
      <FairPanel />
      <AtsPanel />
      <SupportPanel />

      {BEATS.filter((b) => b.text).map((b) => {
        const i = BEATS.indexOf(b)
        const len = b.at[1] - b.at[0]
        return (
          <div
            key={b.id}
            className='off-caption'
            style={anim({
              at: [b.at[0] + len * 0.04, b.at[0] + len * 0.16],
              out: [b.at[1] - len * 0.1, b.at[1]],
              y: 24,
              oy: -16
            })}
          >
            <div className='off-eyebrow'>
              {i} · {b.label}
            </div>
            <p>{b.text}</p>
          </div>
        )
      })}

      <div
        className='off-outro'
        style={anim({ at: inB('finale', 0.4, 0.6), y: 30 })}
      >
        <h2 className='off-title'>Alle happy.</h2>
        <p>So geht Recruiting mit Aivy.</p>
        <div className='off-outro__actions'>
          <Link to='/career/new' className='off-cta'>
            Stelle anlegen
          </Link>
          <button
            type='button'
            onClick={toTop}
            className='off-cta off-cta--ghost'
          >
            Nochmal von vorn
          </button>
        </div>
      </div>
    </div>
  )
}
